import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import config from '../../config';
import './ChristmasParty.css';

// Simple SVG components for illustrations to match the style
const DiscoBall = () => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="50" cy="50" r="40" />
    <path d="M50 10v80 M10 50h80 M20 20l60 60 M20 80l60-60" />
    <path d="M50 10v-10" />
  </svg>
);

const WineGlass = () => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M30 20 Q30 60 50 60 Q70 60 70 20" />
    <path d="M30 20 H70" />
    <path d="M50 60 V90" />
    <path d="M30 90 H70" />
    <path d="M35 30 H65" opacity="0.5" />
  </svg>
);

const Champagne = () => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M40 80 L35 30 L65 30 L60 80 Z" />
    <path d="M35 30 L30 20 L70 20 L65 30" />
    <circle cx="50" cy="50" r="10" />
    <path d="M60 40 L80 10" /> {/* Cork popping */}
  </svg>
);

const Holly = () => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="50" cy="50" r="5" fill="currentColor" />
    <circle cx="40" cy="55" r="5" fill="currentColor" />
    <circle cx="60" cy="55" r="5" fill="currentColor" />
    <path d="M50 45 Q30 20 50 10 Q70 20 50 45" />
    <path d="M40 60 Q10 70 20 90 Q50 80 40 60" />
    <path d="M60 60 Q90 70 80 90 Q50 80 60 60" />
  </svg>
);


const ChristmasParty = () => {
  const { id } = useParams();
  const [eventData, setEventData] = useState(null);
  console.log("Event ID from URL:", eventData);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const hasAttemptedAutoOpen = useRef(false);

  // Device detection and app redirect function
  const handleOpenInApp = useCallback((redirectIfNotFound = false) => {
    // Try to open the app via deep link
    const isProductionHost = window.location.hostname === 'share.outr-connect.com' || window.location.hostname === 'sharing.outr-connect.com';
    const primaryHost = isProductionHost ? window.location.host : 'share.outr-connect.com';
    const fallbackHost = primaryHost === 'sharing.outr-connect.com' ? 'share.outr-connect.com' : 'sharing.outr-connect.com';

    window.location.href = `outrapp://${primaryHost}/share?pageName=eventDetail&eventId=${id}`;

    setTimeout(() => {
      if (document.visibilityState === 'visible') {
        window.location.href = `outrapp://${fallbackHost}/share?pageName=eventDetail&eventId=${id}`;
      }
    }, 500);

    if (redirectIfNotFound) {
      let appOpened = false;

      const handleVisibilityChange = () => {
        if (document.hidden || document.visibilityState === 'hidden') {
          appOpened = true;
        }
      };

      const handleBlur = () => {
        appOpened = true;
      };

      window.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pagehide', handleVisibilityChange);
      window.addEventListener('blur', handleBlur);

      setTimeout(() => {
        window.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pagehide', handleVisibilityChange);
        window.removeEventListener('blur', handleBlur);

        if (!appOpened && document.visibilityState === 'visible') {
          const ua = navigator.userAgent || '';
          const isAndroid = /android/i.test(ua);
          const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

          if (isAndroid) {
            window.location.href = 'https://play.google.com/store/apps/details?id=com.app.outr';
          } else if (isIOS) {
            window.location.href = 'https://apps.apple.com/us/app/outr/id6748447370';
          } else {
            alert('Please visit the App Store or Play Store on your mobile device to download the app.');
          }
        }
      }, 2500);
    }
  }, [id]);

  const [selectedTier, setSelectedTier] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [expandedTiers, setExpandedTiers] = useState({});

  const toggleTierDescription = (tierId, e) => {
    e.stopPropagation();
    setExpandedTiers(prev => ({
      ...prev,
      [tierId]: !prev[tierId]
    }));
  };

  // Set default tier to the first available bookable tier
  useEffect(() => {
    if (eventData?.tiers && eventData.tiers.length > 0) {
      const visibleTiers = eventData.tiers.filter(t => 
        t.publicView !== 2 && t.publicView !== '2' && t.publicView !== 0 && t.publicView !== '0' && t.publicView !== false
      );
      const now = new Date();
      const firstAvailable = visibleTiers.find(t => 
        (Number(t.availableQty) > 0 || (Number(t.capacity || 0) > Number(t.soldQty || 0))) &&
        (!t.salesEndDate || new Date(t.salesEndDate) >= now)
      );
      if (firstAvailable) {
        setSelectedTier(firstAvailable);
      }
    }
  }, [eventData]);

  const handleTierSelect = (tier) => {
    if (tier.availableQty > 0) {
      setSelectedTier(tier);
      setQuantity(1); // Reset quantity on new tier selection
    }
  };

  const handleQuantityChange = (delta) => {
    if (!selectedTier) return;

    const newQty = quantity + delta;
    if (newQty >= 1 && newQty <= selectedTier.availableQty) {
      setQuantity(newQty);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (!val) {
      setEmailError('Email is required');
    } else if (!validateEmail(val)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Auto-redirect for promoted events
  useEffect(() => {
    if (eventData?.isPromoted === 1 && eventData?.bookingUrl) {
      window.location.href = eventData.bookingUrl;
    }
  }, [eventData]);

  // Auto-open app on load if eventData is ready (no store redirection on failure)
  useEffect(() => {
    if (eventData && !hasAttemptedAutoOpen.current) {
      hasAttemptedAutoOpen.current = true;
      handleOpenInApp(false);
    }
  }, [eventData, handleOpenInApp]);

  // Update Document Title and Dynamic Open Graph/SEO Meta Tags
  useEffect(() => {
    if (eventData) {
      // Update page title
      document.title = `${eventData.title} | Outr`;

      // Helper function to update or create meta tags in <head>
      const updateMetaTag = (property, content, isName = false) => {
        if (!content) return;
        const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
        let element = document.querySelector(selector);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute(isName ? 'name' : 'property', property);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      // Parse description (strip HTML tags) and truncate
      const cleanDescription = eventData.description 
        ? eventData.description.replace(/<[^>]*>/g, '').substring(0, 160) 
        : 'Join us for this event on Outr!';

      // Set Open Graph tags
      updateMetaTag('og:title', eventData.title);
      updateMetaTag('og:description', cleanDescription);
      updateMetaTag('og:image', eventData.image || 'https://share.outr-connect.com/logo.png');
      updateMetaTag('og:url', window.location.href);
      updateMetaTag('og:type', 'website');

      // Set Twitter tags
      updateMetaTag('twitter:title', eventData.title);
      updateMetaTag('twitter:description', cleanDescription);
      updateMetaTag('twitter:image', eventData.image || 'https://share.outr-connect.com/logo.png');

      // Set standard description for SEO
      updateMetaTag('description', cleanDescription, true);
    }
  }, [eventData]);

  const handleBuyTickets = async () => {
    // 1. Validation
    if (!selectedTier) {
      alert("Please select a ticket tier.");
      return;
    }
    
    if (!email || emailError) {
      setEmailError('Email is required');
      alert("Please enter a valid email address.");
      return;
    }

    setBookingLoading(true);

    const payload = {
      eventId: eventData._id,
      tierId: selectedTier._id,
      qty: quantity,
      guestEmail: email
    };

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/v1/user/out-off-ticket-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "any-value"
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        // Check for checkoutUrl directly or nested in data
        const checkoutUrl = data.checkoutUrl || data.data?.checkoutUrl;

        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          alert("Booking successful! Check your email for details.");
        }
      } else {
        alert(data.message || "Booking failed. Please try again.");
      }
    } catch (error) {
      console.error("Booking error:", error);
      alert("An error occurred. Please check your connection.");
    } finally {
      setBookingLoading(false);
    }
  };


  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/v1/user/event-preview?id=${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            "ngrok-skip-browser-warning": "any-value"
          }
        });
        const data = await response.json();

        if (data?.data) {
          setEventData(data.data);
        }
      } catch (error) {
        console.error("Error fetching event data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEventData();
    }
  }, [id]);

  // Function to directly redirect to app stores
  const handleDownloadApp = () => {
    const ua = navigator.userAgent || '';
    const isAndroid = /android/i.test(ua);
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isAndroid) {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.app.outr';
    } else if (isIOS) {
      window.location.href = 'https://apps.apple.com/us/app/outr/id6748447370';
    } else {
      // For desktop, you could show a modal or redirect to a landing page
      alert('Please visit the App Store or Play Store on your mobile device to download the app.');
    }
  };

  if (loading) {
    return <div className="christmas-party-container">Loading...</div>;
  }

  if (!eventData) {
    return <div className="christmas-party-container">Event not found</div>;
  }

  const getFormattedPrice = () => {
    if (!eventData) return '';
    const now = new Date();
    const visibleTiers = (eventData.tiers || []).filter(t => 
      t.publicView !== 2 && t.publicView !== '2' && t.publicView !== 0 && t.publicView !== '0' && t.publicView !== false
    );
    const bookableTiers = visibleTiers.filter(t => {
      const hasQty = (Number(t.availableQty) > 0) || (Number(t.capacity || 0) > Number(t.soldQty || 0));
      const notExpired = !t.salesEndDate || new Date(t.salesEndDate) >= now;
      return hasQty && notExpired;
    });

    let priceVal = null;
    if (bookableTiers.length > 0) {
      const prices = bookableTiers.map(t => Number(t.price) || 0);
      priceVal = Math.min(...prices);
    } else if (eventData.price !== undefined && eventData.price !== null) {
      priceVal = Number(eventData.price) || 0;
    } else if (visibleTiers.length > 0) {
      const prices = visibleTiers.map(t => Number(t.price) || 0);
      priceVal = Math.min(...prices);
    } else {
      priceVal = 0;
    }

    if (priceVal === 0) return 'Free';
    const isTiered = eventData.hasMultipleTiers || visibleTiers.length > 1;
    return isTiered ? `From £${priceVal.toFixed(2)}` : `£${priceVal.toFixed(2)}`;
  };

  return (
    <div className="christmas-party-container">
      <div className="event-card">
        {/* Header */}
        <div className="card-header">
          <img src="/image/logo-dark.png" alt="Logo" className="brand-logo" />
          <div className="header-buttons">
            {/* <button className="btn-header" onClick={handleDownloadApp}>Download to join</button> */}
            <button className="btn-header" onClick={() => handleOpenInApp(true)}>Open in app</button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="main-content-scroll">

          {/* Left Side: Hero & Illustrations */}
          <div className="content-left">
            <h1 className="hero-title">{eventData.title || "Christmas Party"}</h1>
            <p className="location-text">📍 {eventData.location?.address || eventData.location?.fullAddress || "Location TBD"}</p>
            <p className="host-text">Hosted by {eventData.host?.name || "Finders Keepers"}</p>

            {eventData.image ? (
              <div className="event-main-image-container">
                <img src={eventData.image} alt={eventData.title} className="event-main-image" />
              </div>
            ) : (
              <div className="illustration-grid">
                <div className="illustration-item"><Holly /></div>
                <div className="illustration-item"><WineGlass /></div>
                <div className="illustration-item"><Holly /></div>

                <div className="illustration-item"><DiscoBall /></div>
                <div className="illustration-item"></div>
                <div className="illustration-item"><Champagne /></div>

                <div className="illustration-item"></div>
                <div className="illustration-item"><GlassToast /></div>
                <div className="illustration-item"><Holly /></div>
              </div>
            )}
          </div>

          {/* Right Side: Details & Actions */}
          {/* Right Side: Details & Actions */}
          <div className="content-right">
            <div className="host-info-card">
              <div className="host-avatar">
                <img src={eventData.host?.profileImage || "https://ui-avatars.com/api/?name=Finders+Keepers&background=random"} alt="Host" />
              </div>
              <div className="host-details">
                <h3>{eventData.title || "Christmas Party"}</h3>
                <div className="tags-container">
                  {eventData.category?.map(cat => (
                    <span key={cat._id} className="category-tag">{cat.name}</span>
                  ))}
                  {eventData.tags?.map((tag, index) => (
                    <span key={index} className="category-tag">#{tag}</span>
                  ))}
                </div>
                <p>
                  {eventData.description || "Join us for an evening of festive fun!"}
                  <span className="tree-icon">🎄</span>
                </p>
              </div>
            </div>

            <div className="logistics-card">
              <div className="logistics-row single-item">
                <span className="icon-text">🎟️ Ticket price</span>
                <span className="value-text">{getFormattedPrice()}</span>
              </div>
              <div className="logistics-row single-item">
                <span className="icon-text">🕒 Date & Time</span>
                <span className="value-text">
                  {new Date(eventData.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  <br />
                  {new Date(eventData.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(eventData.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="logistics-row single-item">
                <span className="icon-text">📍 Location</span>
                <span className="value-text">{eventData.location?.fullAddress || eventData.location?.address || "Location TBD"}</span>
              </div>
            </div>

            {/* Tiers Selection */}
            {eventData.tiers && eventData.tiers.length > 0 && (
              <div className="tiers-section">
                <h4 className="section-title">Select Ticket</h4>
                <div className="tiers-list">
                  {eventData.tiers
                    .filter(tier => tier.publicView !== 2 && tier.publicView !== '2' && tier.publicView !== 0 && tier.publicView !== '0' && tier.publicView !== false)
                    .map((tier) => (
                    <div
                      key={tier._id}
                      className={`tier-card ${selectedTier?._id === tier._id ? 'selected' : ''} ${tier.availableQty === 0 ? 'sold-out' : ''}`}
                      onClick={() => handleTierSelect(tier)}
                    >
                      <div className="tier-info">
                        <span className="tier-name">{tier.name || eventData.title}</span>
                        <span className="tier-capacity">{tier.availableQty > 0 ? `${tier.availableQty} left` : 'Sold Out'}</span>
                        {tier.description && (
                          <div className="tier-description">
                            {tier.description.length > 80 && !expandedTiers[tier._id] ? (
                              <>
                                {tier.description.substring(0, 80)}...
                                <button
                                  className="tier-read-more-btn"
                                  onClick={(e) => toggleTierDescription(tier._id, e)}
                                >
                                  Read more
                                </button>
                              </>
                            ) : (
                              <>
                                {tier.description}
                                {tier.description.length > 80 && (
                                  <button
                                    className="tier-read-more-btn"
                                    onClick={(e) => toggleTierDescription(tier._id, e)}
                                  >
                                    Read less
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="tier-price">
                        {tier.price === 0 ? 'Free' : `£${tier.price}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity and Total Price (Only if a tier is selected) */}
            {selectedTier && (
              <div className="quantity-price-container">
                <div className="quantity-selector">
                  <span className="label">Quantity</span>
                  <div className="qty-controls">
                    <button
                      className="qty-btn"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <span className="qty-value">{quantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= selectedTier.availableQty}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="total-price-display">
                  <span className="label">Total</span>
                  <span className="price-value">
                    {selectedTier.price === 0 ? 'Free' : `£${(selectedTier.price * quantity).toFixed(2)}`}
                  </span>
                </div>
              </div>
            )}

            {/* Email Input Field */}
            <div className="email-input-container" style={{ marginBottom: '15px' }}>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: emailError ? '1px solid red' : '1px solid #e5e7eb',
                  fontSize: '15px',
                  backgroundColor: '#f9fafb',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {emailError && <span style={{ color: 'red', fontSize: '12px', marginTop: '5px', display: 'block' }}>{emailError}</span>}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                className="btn-primary"
                onClick={handleBuyTickets}
                disabled={bookingLoading}
                style={{ opacity: bookingLoading ? 0.7 : 1 }}
              >
                {bookingLoading ? 'Processing...' : 'Buy tickets online!'}
              </button>

              <button className="btn-primary" onClick={() => handleOpenInApp(true)} style={{ background: 'white', color: '#eb4e23', border: '1px solid #eb4e23', marginTop: '10px' }}>Open Event in App</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper for the Toasting Glasses
const GlassToast = () => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M30 60 L40 40 L50 40 L40 60 Z" />
    <path d="M40 60 L30 90" />
    <path d="M20 90 L40 90" />

    <path d="M70 60 L60 40 L50 40 L60 60 Z" />
    <path d="M60 60 L70 90" />
    <path d="M60 90 L80 90" />
  </svg>
)

export default ChristmasParty;
